/**
 * GoKwik cart — visual pack bundle labels (never hides line items — GoKwik checkout needs them).
 */

(function initGokwikPackCart() {
  if (window.__gokwikPackCartInit) return;
  window.__gokwikPackCartInit = true;

  const BUNDLE_CLASS = 'gokwik-pack-bundle';
  const GROUPED_CLASS = 'gokwik-pack-item--grouped';

  function isGokwikActive() {
    return typeof window.isGokwikCartActive === 'function' && window.isGokwikCartActive();
  }

  async function fetchCartJson() {
    const cartUrl = window.routes?.cart_url || '/cart';
    const response = await fetch(`${cartUrl}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Could not fetch cart');
    return response.json();
  }

  function groupPackItems(cart) {
    const groups = new Map();

    (cart.items || []).forEach((item) => {
      const packKey = item.properties?._pack_key;
      if (!packKey) return;

      if (!groups.has(packKey)) {
        groups.set(packKey, {
          key: packKey,
          label: item.properties._pack_label || `Pack of ${item.properties._pack_size || '?'}`,
          items: [],
          subtotal: 0,
          lineKeys: [],
        });
      }

      const group = groups.get(packKey);
      group.items.push(item);
      group.subtotal += item.final_line_price;
      group.lineKeys.push(item.key);
    });

    return Array.from(groups.values());
  }

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }

    return (cents / 100).toFixed(2);
  }

  function findGoKwikRoots() {
    const selectors = [
      '[class*="gokwik"]',
      '[id*="gokwik"]',
      '[class*="Gokwik"]',
      '[class*="kwik-cart"]',
      '[id*="kwik-cart"]',
      '[class*="KwikCart"]',
    ];

    const roots = new Set();
    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((node) => {
        if (node instanceof HTMLElement) roots.add(node);
      });
    });

    return Array.from(roots);
  }

  function findCartItemElements(root) {
    const selectors = [
      '[data-line-key]',
      '[data-cart-item-key]',
      '[class*="cart-item"]',
      '[class*="CartItem"]',
      '[class*="line-item"]',
      '[class*="LineItem"]',
      '[class*="cartItem"]',
    ];

    const elements = new Set();

    selectors.forEach((selector) => {
      root.querySelectorAll(selector).forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.classList.contains(BUNDLE_CLASS)) return;
        if (node.closest(`.${BUNDLE_CLASS}`)) return;
        elements.add(node);
      });
    });

    return Array.from(elements).filter((element) => {
      const nested = element.querySelector(
        '[data-line-key], [data-cart-item-key], [class*="cart-item"], [class*="line-item"]'
      );
      return !nested;
    });
  }

  function matchesCartItem(element, item) {
    const lineKey = element.dataset.lineKey || element.dataset.cartItemKey;
    if (lineKey && lineKey === item.key) return true;

    const html = element.outerHTML;
    if (html.includes(item.key)) return true;
    if (html.includes(String(item.variant_id))) return true;

    const text = element.textContent || '';
    const shade = item.properties?._pack_shade || item.variant_title;
    return text.includes(item.product_title) && text.includes(shade);
  }

  function buildBundleElement(group) {
    const bundle = document.createElement('div');
    bundle.className = BUNDLE_CLASS;
    bundle.dataset.packKey = group.key;
    bundle.setAttribute('aria-hidden', 'true');

    const sortedItems = group.items.slice().sort((a, b) => {
      const slotA = parseInt(a.properties?._pack_slot || '0', 10);
      const slotB = parseInt(b.properties?._pack_slot || '0', 10);
      return slotA - slotB;
    });

    const shades = sortedItems
      .map((item, index) => {
        const shade = item.properties?._pack_shade || item.variant_title;
        const slot = item.properties?._pack_slot || String(index + 1);
        return `<li>Unit ${slot}: ${shade}</li>`;
      })
      .join('');

    bundle.innerHTML = `
      <div class="gokwik-pack-bundle__inner">
        <div class="gokwik-pack-bundle__details">
          <p class="gokwik-pack-bundle__label">${group.label}</p>
          <ul class="gokwik-pack-bundle__shades">${shades}</ul>
        </div>
        <button
          type="button"
          class="gokwik-pack-bundle__remove link underlined-link"
          data-gokwik-pack-remove
          data-line-keys="${group.lineKeys.join(',')}"
        >
          Remove pack
        </button>
      </div>
    `;

    return bundle;
  }

  function resetGrouping(root) {
    root.querySelectorAll(`.${BUNDLE_CLASS}`).forEach((node) => node.remove());
    root.querySelectorAll(`.${GROUPED_CLASS}`).forEach((node) => {
      node.classList.remove(GROUPED_CLASS);
    });
  }

  function resetAllGrouping() {
    findGoKwikRoots().forEach(resetGrouping);
  }

  async function applyPackGrouping() {
    if (!isGokwikActive()) return;

    let cart;
    try {
      cart = await fetchCartJson();
    } catch (error) {
      return;
    }

    const groups = groupPackItems(cart);
    const roots = findGoKwikRoots();
    if (!roots.length) return;

    roots.forEach((root) => {
      resetGrouping(root);
      if (!groups.length) return;

      const itemElements = findCartItemElements(root);

      groups.forEach((group) => {
        const matchedElements = [];

        group.items.forEach((item) => {
          const match = itemElements.find((element) => matchesCartItem(element, item));
          if (match && !matchedElements.includes(match)) matchedElements.push(match);
        });

        if (!matchedElements.length) return;

        matchedElements[0].insertAdjacentElement('beforebegin', buildBundleElement(group));
        matchedElements.forEach((element) => element.classList.add(GROUPED_CLASS));
      });
    });
  }

  let applyTimer;

  function scheduleApply(delay = 300) {
    clearTimeout(applyTimer);
    applyTimer = setTimeout(() => {
      applyPackGrouping().catch(() => {});
    }, delay);
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-gokwik-pack-remove]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    const keys = (button.dataset.lineKeys || '')
      .split(',')
      .map((key) => key.trim())
      .filter(Boolean);

    if (!keys.length) return;

    button.disabled = true;

    const updates = {};
    keys.forEach((key) => {
      updates[key] = 0;
    });

    try {
      const base = window.routes?.cart_update_url || '/cart/update';
      const cartUpdateUrl = base.endsWith('.js') ? base : `${base}.js`;

      const response = await fetch(cartUpdateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.description || 'Could not remove pack.');
      }

      if (typeof window.refreshCartUI === 'function') {
        await window.refreshCartUI({ openDrawer: true });
      } else {
        scheduleApply(100);
      }
    } catch (error) {
      console.error(error);
      button.disabled = false;
    }
  });

  document.documentElement.addEventListener('gokwik:cart:refresh', () => scheduleApply());

  window.applyGokwikPackGrouping = () => scheduleApply(0);
  window.resetGokwikPackGrouping = resetAllGrouping;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleApply(800));
  } else {
    scheduleApply(800);
  }
})();